import React, { useState, useEffect } from 'react';
import { apiClient } from '../../utils/apiClient';

const BASE = process.env.REACT_APP_API_BASE_URL;

export default function ProfesseurProgres() {
    const [students, setStudents] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [filter, setFilter]     = useState('');
    const [classeFilter, setClasse] = useState('');
    const [sortKey, setSortKey]   = useState('name');
    const [sortAsc, setSortAsc]   = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res  = await apiClient(`${BASE}/professeur_classes.php?action=all_progress`);
                const json = await res.json();
                if (json.success) setStudents(json.students);
            } finally { setLoading(false); }
        };
        load();
    }, []);

    const classes = [...new Set(students.map(s => s.classe_nom))].sort();

    const sort = (key) => {
        if (sortKey === key) setSortAsc(v => !v);
        else { setSortKey(key); setSortAsc(true); }
    };

    const filtered = students
        .filter(s => {
            const q = filter.toLowerCase();
            const matchText = !q || s.name.toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q);
            const matchClass = !classeFilter || s.classe_nom === classeFilter;
            return matchText && matchClass;
        })
        .sort((a, b) => {
            let va = a[sortKey] ?? '';
            let vb = b[sortKey] ?? '';
            if (typeof va === 'string') va = va.toLowerCase();
            if (typeof vb === 'string') vb = vb.toLowerCase();
            if (va < vb) return sortAsc ? -1 : 1;
            if (va > vb) return sortAsc ? 1 : -1;
            return 0;
        });

    const SortHeader = ({ label, k }) => (
        <th onClick={() => sort(k)}
            className="p-3 text-center cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
            {label} {sortKey === k ? (sortAsc ? '↑' : '↓') : ''}
        </th>
    );

    if (loading) return <div className="text-center py-20 text-gray-400">Chargement…</div>;

    return (
        <div>
            <div className="flex flex-wrap items-center gap-3 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mr-auto">
                    Progrès des élèves
                    <span className="ml-2 text-sm font-normal text-gray-400">({filtered.length} élève{filtered.length !== 1 ? 's' : ''})</span>
                </h2>
                {classes.length > 1 && (
                    <select value={classeFilter} onChange={e => setClasse(e.target.value)}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                        <option value="">Toutes les classes</option>
                        {classes.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                )}
                <input type="text" placeholder="Rechercher un élève…" value={filter}
                    onChange={e => setFilter(e.target.value)}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 w-52" />
            </div>

            {students.length === 0 ? (
                <div className="text-center py-20 text-gray-400">
                    <p className="text-lg mb-2">Aucun élève</p>
                    <p className="text-sm">Ajoutez des élèves dans vos classes pour voir leur progression ici.</p>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                        <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 text-xs uppercase">
                            <tr>
                                <th onClick={() => sort('name')}
                                    className="p-3 text-left cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                                    Nom {sortKey === 'name' ? (sortAsc ? '↑' : '↓') : ''}
                                </th>
                                <th onClick={() => sort('classe_nom')}
                                    className="p-3 text-left cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                                    Classe {sortKey === 'classe_nom' ? (sortAsc ? '↑' : '↓') : ''}
                                </th>
                                <SortHeader label="Exercices" k="skills_done" />
                                <SortHeader label="Flashcards" k="flashcards_count" />
                                <SortHeader label="Quizzes" k="quizzes_count" />
                                <th className="p-3 text-center">Activité globale</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                            {filtered.map(s => {
                                const total = parseInt(s.skills_done) + parseInt(s.flashcards_count) + parseInt(s.quizzes_count);
                                const maxTotal = Math.max(...filtered.map(x => parseInt(x.skills_done) + parseInt(x.flashcards_count) + parseInt(x.quizzes_count)), 1);
                                const pct = Math.round((total / maxTotal) * 100);
                                return (
                                    <tr key={`${s.id}-${s.classe_id}`} className="hover:bg-gray-50 dark:hover:bg-white/5">
                                        <td className="p-3 font-medium text-gray-800 dark:text-gray-200">{s.name}</td>
                                        <td className="p-3">
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">
                                                {s.classe_nom}
                                            </span>
                                        </td>
                                        <td className="p-3 text-center">
                                            <span className={`font-medium ${parseInt(s.skills_done) > 0 ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`}>
                                                {s.skills_done}
                                            </span>
                                        </td>
                                        <td className="p-3 text-center">
                                            <span className={`font-medium ${parseInt(s.flashcards_count) > 0 ? 'text-cyan-600 dark:text-cyan-400' : 'text-gray-400'}`}>
                                                {s.flashcards_count}
                                            </span>
                                        </td>
                                        <td className="p-3 text-center">
                                            <span className={`font-medium ${parseInt(s.quizzes_count) > 0 ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400'}`}>
                                                {s.quizzes_count}
                                            </span>
                                        </td>
                                        <td className="p-3">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                                    <div className="h-full bg-teal-500 rounded-full transition-all"
                                                        style={{ width: `${pct}%` }} />
                                                </div>
                                                <span className="text-xs text-gray-400 w-8 text-right">{total}</span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
